import { SortationCenterError } from "@deliverr/sortation-client";
import {
  createDangerNotification,
  createSuccessNotification,
  notificationsState,
  SoundFx,
} from "@deliverr/ui-facility";
import { useCommonFlow } from "@deliverr/ui-facility/lib-facility/flow/useCommonFlow";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useAsyncFn, useUnmount } from "react-use";
import { useRecoilValue } from "recoil";
import { sortationIdState } from "sortation/base/sortationIdState";
import { useSortationCenterError } from "sortation/base/useSortationCenterError";
import { TOAST_AUTO_CLOSE } from "sortation/utils/toastAutoClose";
import { OutboundModal } from "../Outbound/modals";
import { useSortationModal } from "sortation/components/SortationModal";
import { isGaylord } from "sortation/utils/stringValidation";
import { useSortationClient } from "sortation/base/useSortationClient";

export const useShipToStoreLabelScan = () => {
  const { formatMessage } = useIntl();
  const notifications = useRecoilValue(notificationsState);
  const { showModal } = useSortationModal();
  const sortationClient = useSortationClient();
  const { playSfx, addAutoCloseNotification, resetNotifications, emitFlash, customResponse } = useCommonFlow();
  const [barcode, setBarcode] = useState<string>("");
  const { handleSortationCenterError } = useSortationCenterError();

  const showInvalidModal = () => {
    emitFlash("DANGER");
    showModal(OutboundModal.INVALID_GAYLORD_CODE, {});
  };

  const sortationId = useRecoilValue(sortationIdState);

  const errorResponse = useMemo(() => customResponse(SoundFx.ERROR_FAST, "DANGER"), [customResponse]);

  useUnmount(() => {
    resetNotifications();
  });

  const [handleShipToStoreLabelScanState, handleShipToStoreLabelScan] = useAsyncFn(
    async (scannedBarcode: string) => {
      resetNotifications();
      if (!scannedBarcode) {
        emitFlash("DANGER");
        showModal(OutboundModal.EMPTY_GAYLORD_CODE, {});
        return;
      }

      setBarcode(scannedBarcode);

      if (!isGaylord(scannedBarcode)) {
        showInvalidModal();
        return;
      }

      try {
        await sortationClient.generateShipToStoreLabels(scannedBarcode, sortationId!);
        playSfx(SoundFx.SUCCESS);
        emitFlash("SUCCESS");
        addAutoCloseNotification(
          createSuccessNotification(
            formatMessage(
              {
                id: "sortation.successScan",
                defaultMessage: `{scannedBarcode} scanned.`,
              },
              { scannedBarcode }
            )
          ),
          TOAST_AUTO_CLOSE
        );
      } catch (err) {
        if (err.message === SortationCenterError.SORTATION_CENTER_NOT_FOUND) {
          handleSortationCenterError();
          return;
        }
        errorResponse();
        addAutoCloseNotification(
          createDangerNotification(
            formatMessage({
              id: "sortation.ShipToStoreLabel.danger",
              defaultMessage: "Remove unrecognized package.",
            })
          ),
          TOAST_AUTO_CLOSE
        );
      } finally {
        setBarcode("");
      }
      return;
    },
    [notifications, sortationId]
  );

  return {
    barcode,
    setBarcode,
    handleShipToStoreLabelScan,
    handleShipToStoreLabelScanState,
  };
};
